import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ADMIN_SUGGESTION_COLUMNS, BOARD_PUBLIC_COLUMNS } from '@/idea-board/lib/constants'
import { ideaBoardDisplayName } from '@/idea-board/lib/ideaBoardUser'
import { getFileExtension, validateIdeaAttachmentList } from '@/idea-board/lib/attachmentValidation'

export const IDEA_SUGGESTIONS_BOARD_KEY = ['idea-board', 'suggestions', 'board']
export const IDEA_SUGGESTIONS_ADMIN_KEY = ['idea-board', 'suggestions', 'admin']
export const IDEA_VOTES_KEY = ['idea-board', 'votes']
export const IDEA_PROFILES_KEY = ['idea-board', 'profiles']
export const IDEA_CATEGORIES_KEY = ['idea-board', 'categories']
export const IDEA_ATTACHMENTS_KEY = ['idea-board', 'attachments']

export const IDEA_ATTACHMENT_BUCKET = 'idea-board-attachments'
export const ATTACHMENT_SELECT_COLUMNS =
  'id, suggestion_id, storage_path, original_filename, mime_type, file_size, created_at, created_by'

async function fetchBoardSuggestions() {
  const { data, error } = await supabase
    .from('idea_board_suggestions')
    .select(BOARD_PUBLIC_COLUMNS)
    .eq('is_hidden', false)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

async function fetchAdminSuggestions() {
  const { data, error } = await supabase
    .from('idea_board_suggestions')
    .select(ADMIN_SUGGESTION_COLUMNS)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

async function fetchVotes() {
  const { data, error } = await supabase
    .from('idea_board_votes')
    .select('id, suggestion_id, user_id, value, created_at')
  if (error) throw error
  return data ?? []
}

async function fetchProfilesForAdmin() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email')
  if (error) throw error
  return data ?? []
}

export function useBoardSuggestions() {
  return useQuery({
    queryKey: IDEA_SUGGESTIONS_BOARD_KEY,
    queryFn: fetchBoardSuggestions,
    staleTime: 30_000,
  })
}

export function useAdminSuggestions(enabled = true) {
  return useQuery({
    queryKey: IDEA_SUGGESTIONS_ADMIN_KEY,
    queryFn: fetchAdminSuggestions,
    enabled,
    staleTime: 30_000,
  })
}

export function useIdeaBoardVotes() {
  return useQuery({
    queryKey: IDEA_VOTES_KEY,
    queryFn: fetchVotes,
    staleTime: 15_000,
  })
}

export function useIdeaBoardProfiles(enabled = true) {
  return useQuery({
    queryKey: IDEA_PROFILES_KEY,
    queryFn: fetchProfilesForAdmin,
    enabled,
    staleTime: 5 * 60_000,
  })
}

async function deleteIdeaSuggestionWithAttachments(suggestionId) {
  const { data: attachments, error: fetchError } = await supabase
    .from('idea_board_attachments')
    .select('storage_path')
    .eq('suggestion_id', suggestionId)

  if (fetchError) throw fetchError

  if (attachments?.length) {
    const { error: storageError } = await supabase.storage
      .from(IDEA_ATTACHMENT_BUCKET)
      .remove(attachments.map((a) => a.storage_path))
    if (storageError) throw storageError
  }

  const { error } = await supabase.from('idea_board_suggestions').delete().eq('id', suggestionId)
  if (error) throw error
}

async function rollbackSuggestionSubmit(suggestionId) {
  await deleteIdeaSuggestionWithAttachments(suggestionId)
}

export async function uploadIdeaAttachment(suggestionId, file, userId) {
  const attachmentId = crypto.randomUUID()
  const ext = getFileExtension(file.name).replace(/^\./, '') || 'bin'
  const storagePath = `${suggestionId}/${attachmentId}.${ext}`

  const { error: uploadError } = await supabase.storage
    .from(IDEA_ATTACHMENT_BUCKET)
    .upload(storagePath, file, { upsert: false, contentType: file.type || undefined })

  if (uploadError) throw uploadError

  const { data, error: insertError } = await supabase
    .from('idea_board_attachments')
    .insert({
      id: attachmentId,
      suggestion_id: suggestionId,
      storage_path: storagePath,
      original_filename: file.name,
      mime_type: file.type || '',
      file_size: file.size,
      created_by: userId,
    })
    .select(ATTACHMENT_SELECT_COLUMNS)
    .single()

  if (insertError) {
    await supabase.storage.from(IDEA_ATTACHMENT_BUCKET).remove([storagePath])
    throw insertError
  }

  return data
}

export async function getIdeaAttachmentSignedUrl(storagePath, expiresIn = 3600) {
  const { data, error } = await supabase.storage
    .from(IDEA_ATTACHMENT_BUCKET)
    .createSignedUrl(storagePath, expiresIn)
  if (error) throw error
  return data.signedUrl
}

export function getIdeaAttachmentPreviewPath(attachmentId) {
  return `/idea-board/attachments/${attachmentId}/preview`
}

export async function fetchIdeaAttachmentById(attachmentId) {
  const { data, error } = await supabase
    .from('idea_board_attachments')
    .select(ATTACHMENT_SELECT_COLUMNS)
    .eq('id', attachmentId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function downloadIdeaAttachment(attachment) {
  const signedUrl = await getIdeaAttachmentSignedUrl(attachment.storage_path)
  const res = await fetch(signedUrl)
  if (!res.ok) throw new Error('Failed to download file')

  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = attachment.original_filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}

async function fetchAttachmentsForSuggestions(suggestionIds) {
  if (!suggestionIds?.length) return []
  const { data, error } = await supabase
    .from('idea_board_attachments')
    .select(ATTACHMENT_SELECT_COLUMNS)
    .in('suggestion_id', suggestionIds)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data ?? []
}

export function useIdeaBoardAttachments(suggestionIds) {
  const ids = useMemo(
    () => [...new Set((suggestionIds || []).filter(Boolean))].sort(),
    [suggestionIds],
  )

  return useQuery({
    queryKey: [...IDEA_ATTACHMENTS_KEY, ids],
    queryFn: () => fetchAttachmentsForSuggestions(ids),
    enabled: ids.length > 0,
    staleTime: 30_000,
  })
}

export function useSubmitSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ title, description, category, user, profile, files = [] }) => {
      const fileList = files || []
      const validationError = validateIdeaAttachmentList(fileList)
      if (validationError) throw new Error(validationError)

      const submitterName = ideaBoardDisplayName(profile, user)
      const { data, error } = await supabase
        .from('idea_board_suggestions')
        .insert({
          title: title.trim(),
          description: description.trim(),
          category,
          is_anonymous: true,
          submitter_name: submitterName,
          created_by: user.id,
        })
        .select('id')
        .single()
      if (error) throw error

      const suggestionId = data.id

      try {
        for (const file of fileList) {
          await uploadIdeaAttachment(suggestionId, file, user.id)
        }
      } catch (uploadErr) {
        await rollbackSuggestionSubmit(suggestionId)
        throw uploadErr
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['idea-board'] })
    },
  })
}

export function useDeleteOwnSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id) => {
      await deleteIdeaSuggestionWithAttachments(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['idea-board'] })
    },
  })
}

export function useAdminUpdateSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const { error } = await supabase.from('idea_board_suggestions').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['idea-board'] })
    },
  })
}

export function useAdminDeleteSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id) => {
      await deleteIdeaSuggestionWithAttachments(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['idea-board'] })
    },
  })
}

export function useIdeaBoardVoting(userId) {
  const queryClient = useQueryClient()
  const { data: allVotes = [] } = useIdeaBoardVotes()

  const { scores, userVotes } = useMemo(() => {
    const scoresMap = {}
    const userVotesMap = {}
    allVotes.forEach((v) => {
      scoresMap[v.suggestion_id] = (scoresMap[v.suggestion_id] || 0) + v.value
      if (v.user_id === userId) {
        userVotesMap[v.suggestion_id] = v
      }
    })
    return { scores: scoresMap, userVotes: userVotesMap }
  }, [allVotes, userId])

  const handleVote = useCallback(
    async (suggestionId, value) => {
      const existing = userVotes[suggestionId]
      if (existing) {
        if (existing.value === value) {
          const { error } = await supabase.from('idea_board_votes').delete().eq('id', existing.id)
          if (error) throw error
        } else {
          const { error } = await supabase
            .from('idea_board_votes')
            .update({ value })
            .eq('id', existing.id)
          if (error) throw error
        }
      } else {
        const { error } = await supabase.from('idea_board_votes').insert({
          suggestion_id: suggestionId,
          user_id: userId,
          value,
        })
        if (error) throw error
      }
      queryClient.invalidateQueries({ queryKey: IDEA_VOTES_KEY })
    },
    [userVotes, userId, queryClient],
  )

  return { scores, userVotes, allVotes, handleVote }
}

async function fetchCategories() {
  const { data, error } = await supabase
    .from('idea_board_categories')
    .select('id, name')
    .order('name', { ascending: true })
  if (error) throw error
  return data ?? []
}

export function useIdeaBoardCategories() {
  return useQuery({
    queryKey: IDEA_CATEGORIES_KEY,
    queryFn: fetchCategories,
    staleTime: 60_000,
  })
}

/** @returns {string[]} */
export function useIdeaBoardCategoryNames() {
  const { data: categories = [] } = useIdeaBoardCategories()
  return useMemo(() => categories.map((c) => c.name), [categories])
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ name }) => {
      const { data, error } = await supabase
        .from('idea_board_categories')
        .insert({ name: name.trim() })
        .select('id, name')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDEA_CATEGORIES_KEY })
    },
  })
}

export function useUpdateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, name }) => {
      const patch = {}
      if (name !== undefined) patch.name = name.trim()
      const { error } = await supabase.from('idea_board_categories').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDEA_CATEGORIES_KEY })
      queryClient.invalidateQueries({ queryKey: ['idea-board', 'suggestions'] })
    },
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('idea_board_categories').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDEA_CATEGORIES_KEY })
    },
  })
}
